import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios';
import { Modal, Button, Form, Alert, InputGroup, OverlayTrigger, Tooltip, Pagination } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as XLSX from 'xlsx';
import MitraServices from './MitraServices';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';


// Utility functions untuk image cropping
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}


function rotateSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

// ===== 1. UTILITY FUNCTIONS =====
// Ganti fungsi getCroppedImg yang sudah ada dengan ini:

// ===== 1. FUNGSI getCroppedImg DENGAN DEBUG =====
// Ganti fungsi getCroppedImg yang sudah ada dengan ini:

// ===== 3. UTILITY FUNCTIONS UNTUK REACT-IMAGE-CROP =====
// Ganti semua utility functions dengan ini:

// Convert crop percentage to pixels
const getCroppedImg = (image, crop, fileName) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  canvas.width = crop.width * scaleX;
  canvas.height = crop.height * scaleY;

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width * scaleX,
    crop.height * scaleY
  );

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          console.error('Canvas is empty');
          return;
        }
        blob.name = fileName;
        resolve(blob);
      },
      'image/jpeg',
      0.9
    );
  });
};

// Get default crop untuk berbagai aspect ratio
const getDefaultCrop = (aspectRatio, cropShape) => {
  if (cropShape === 'round') {
    // Untuk foto profil (bulat)
    return {
      unit: 'px',
      x: 25,
      y: 25,
      width: 200,
      height: 200,
      aspect: 1
    };
  } else if (aspectRatio === 16/9) {
    // Untuk foto KTP (landscape)
    return {
      unit: 'px',
      x: 25,
      y: 25,
      width: 320,
      height: 180,
      aspect: 16/9
    };
  } else if (aspectRatio === 3/4) {
    // Untuk foto SKCK (portrait)
    return {
      unit: 'px',
      x: 25,
      y: 25,
      width: 240,
      height: 320,
      aspect: 3/4
    };
  } else {
    // Default
    return {
      unit: 'px',
      x: 25,
      y: 25,
      width: 200,
      height: 200,
      aspect: aspectRatio
    };
  }
};

// Image Cropper Component
// Image Cropper Component with Zoom Out capability
// ===== 2. IMAGE CROPPER COMPONENT =====
// Ganti component ImageCropper yang sudah ada dengan ini:

// ===== 4. IMAGE CROPPER COMPONENT BARU =====
// Ganti SELURUH component ImageCropper dengan ini:

const ImageCropper = ({ 
  imageUrl, 
  onCropComplete, 
  show, 
  onHide,
  title = "Crop Image",
  aspectRatio = 1,
  cropShape = "rect"
}) => {
  const [crop, setCrop] = useState(getDefaultCrop(aspectRatio, cropShape));
  const [completedCrop, setCompletedCrop] = useState(null);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const imgRef = useRef(null);

  // Reset when modal opens
  useEffect(() => {
    if (show) {
      setCrop(getDefaultCrop(aspectRatio, cropShape));
      setCompletedCrop(null);
      setScale(1);
      setRotate(0);
    }
  }, [show, aspectRatio, cropShape]);

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    const defaultCrop = getDefaultCrop(aspectRatio, cropShape);
    
    // Adjust crop to fit image
    const maxWidth = Math.min(defaultCrop.width, width - 50);
    const maxHeight = Math.min(defaultCrop.height, height - 50);
    
    const newCrop = {
      ...defaultCrop,
      width: maxWidth,
      height: cropShape === 'round' ? maxWidth : maxHeight,
      x: (width - maxWidth) / 2,
      y: (height - (cropShape === 'round' ? maxWidth : maxHeight)) / 2
    };
    
    setCrop(newCrop);
    console.log('Image loaded, crop set:', newCrop);
  };

  const handleCropSave = async () => {
    console.log('=== SAVING CROP ===');
    console.log('Completed crop:', completedCrop);
    console.log('Scale:', scale);
    console.log('Rotate:', rotate);

    if (!completedCrop || !imgRef.current) {
      alert('Belum ada area yang dipilih untuk di-crop');
      return;
    }

    try {
      const croppedImageBlob = await getCroppedImg(
        imgRef.current,
        completedCrop,
        'cropped-image.jpg'
      );

      console.log('Crop successful, blob size:', croppedImageBlob.size);
      onCropComplete(completedCrop, croppedImageBlob);
      onHide();
    } catch (error) {
      console.error('Error creating crop:', error);
      alert('Gagal memotong gambar: ' + error.message);
    }
  };

  const resetCrop = () => {
    setCrop(getDefaultCrop(aspectRatio, cropShape));
    setCompletedCrop(null);
    setScale(1);
    setRotate(0);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-center mb-3">
          <small className="text-muted">
            Seret sudut kotak untuk mengubah ukuran crop area. Seret tengah kotak untuk memindahkan posisi.
          </small>
        </div>

        <div 
          style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: '400px',
            backgroundColor: '#f8f9fa',
            border: '2px dashed #dee2e6',
            borderRadius: '8px'
          }}
        >
          {imageUrl && (
            <ReactCrop
              crop={crop}
              onChange={(newCrop) => setCrop(newCrop)}
              onComplete={(newCrop) => setCompletedCrop(newCrop)}
              aspect={cropShape === 'round' ? 1 : aspectRatio}
              circularCrop={cropShape === 'round'}
              keepSelection={true}
              style={{
                maxWidth: '100%',
                maxHeight: '400px'
              }}
            >
              <img
                ref={imgRef}
                alt="Crop me"
                src={imageUrl}
                style={{
                  transform: `scale(${scale}) rotate(${rotate}deg)`,
                  maxWidth: '100%',
                  maxHeight: '400px'
                }}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          )}
        </div>

        <div className="mt-4">
          {/* Scale Control */}
          <div className="row mb-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="small fw-bold">
                  Scale: {Math.round(scale * 100)}%
                </Form.Label>
                <div className="d-flex align-items-center gap-2">
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => setScale(Math.max(0.5, scale - 0.1))}
                    disabled={scale <= 0.5}
                  >
                    <Icon icon="mdi:minus" />
                  </Button>
                  <Form.Range
                    min={0.5}
                    max={3}
                    step={0.1}
                    value={scale}
                    onChange={(e) => setScale(Number(e.target.value))}
                    className="flex-grow-1"
                  />
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => setScale(Math.min(3, scale + 0.1))}
                    disabled={scale >= 3}
                  >
                    <Icon icon="mdi:plus" />
                  </Button>
                </div>
                <div className="d-flex justify-content-between">
                  <small className="text-muted">50%</small>
                  <small className="text-muted">300%</small>
                </div>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="small fw-bold">
                  Rotate: {rotate}°
                </Form.Label>
                <div className="d-flex align-items-center gap-2">
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => setRotate(rotate - 90)}
                  >
                    <Icon icon="mdi:rotate-left" />
                  </Button>
                  <Form.Range
                    min={-180}
                    max={180}
                    step={1}
                    value={rotate}
                    onChange={(e) => setRotate(Number(e.target.value))}
                    className="flex-grow-1"
                  />
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => setRotate(rotate + 90)}
                  >
                    <Icon icon="mdi:rotate-right" />
                  </Button>
                </div>
                <div className="d-flex justify-content-between">
                  <small className="text-muted">-180°</small>
                  <small className="text-muted">180°</small>
                </div>
              </Form.Group>
            </div>
          </div>

          {/* Crop Info */}
          {completedCrop && (
            <div className="mb-3">
              <small className="text-success">
                <Icon icon="mdi:check-circle" className="me-1" />
                Crop Area: {Math.round(completedCrop.width)} × {Math.round(completedCrop.height)} pixels
              </small>
            </div>
          )}

          {/* Control Buttons */}
          <div className="d-flex gap-2 justify-content-center">
            <Button variant="outline-secondary" onClick={resetCrop}>
              <Icon icon="mdi:refresh" className="me-1" />
              Reset
            </Button>
            <Button 
              variant="outline-primary" 
              onClick={() => setScale(1)}
            >
              <Icon icon="mdi:fit-to-page" className="me-1" />
              Fit
            </Button>
            <Button 
              variant="outline-info" 
              onClick={() => {
                setScale(1);
                setRotate(0);
              }}
            >
              <Icon icon="mdi:image-size-select-actual" className="me-1" />
              Original
            </Button>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          <Icon icon="mdi:close" className="me-1" />
          Batal
        </Button>
        <Button 
          variant="primary" 
          onClick={handleCropSave}
          disabled={!completedCrop}
        >
          <Icon icon="mdi:crop" className="me-1" />
          Crop & Simpan
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
const MitraList = () => {
    const [mitras, setMitras] = useState([]);
    const [filteredMitras, setFilteredMitras] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [paginatedMitras, setPaginatedMitras] = useState([]);
    
    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [activeDetailTab, setActiveDetailTab] = useState('info');
    
    // Form states
    const [currentMitra, setCurrentMitra] = useState({
        nama_lengkap: '',
        jenis_kelamin: '',
        nomor_wa: '',
        alamat: '',
        url_foto_skck: '',
        url_foto_ktp: '',
        foto_profil: '',
        password: '',
        radius: 5000,
        status: 'active',
        asal_pb: '',
        preferences: {
            gender: ['Pria', 'Wanita']
        }
    });

    // File states (keep existing for backward compatibility)
    const [fileskck, setFileskck] = useState(null);
    const [fileKtp, setFileKtp] = useState(null);
    const [fotoProfil, setFotoProfil] = useState(null);
    const [excelFile, setExcelFile] = useState(null);
    
    // File preview states (keep existing for backward compatibility)
    const [fileskckPreview, setFileskckPreview] = useState(null);
    const [fileKtpPreview, setFileKtpPreview] = useState(null);
    const [fotoProfilPreview, setFotoProfilPreview] = useState(null);

    // Cropper states
    const [cropperStates, setCropperStates] = useState({
        foto_profil: {
            selectedFile: null,
            imagePreview: null,
            showCropper: false,
            originalImageUrl: null
        },
        foto_ktp: {
            selectedFile: null,
            imagePreview: null,
            showCropper: false,
            originalImageUrl: null
        },
        foto_skck: {
            selectedFile: null,
            imagePreview: null,
            showCropper: false,
            originalImageUrl: null
        }
    });

    // Import related states
    const [importValidationErrors, setImportValidationErrors] = useState([]);
    const [importData, setImportData] = useState([]);
    const [isImporting, setIsImporting] = useState(false);
    const fileInputRef = useRef(null);
    const [isAddingMitra, setIsAddingMitra] = useState(false);
const [isEditingMitra, setIsEditingMitra] = useState(false);
const [isDeletingMitra, setIsDeletingMitra] = useState(false);


    // Base URL for API
const API_BASE_URL = 'https://api.tomassage.id/api';
    // Upload service URL
    const UPLOAD_URL = 'https://api.tomassage.id/api/upload';

    // Pagination calculation
    const calculatePagination = (data) => {
        const total = Math.ceil(data.length / itemsPerPage);
        setTotalPages(total);
        
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginated = data.slice(startIndex, endIndex);
        
        setPaginatedMitras(paginated);
    };

    // Handle pagination when filtered data or page changes
    useEffect(() => {
        calculatePagination(filteredMitras);
    }, [filteredMitras, currentPage, itemsPerPage]);

    // Reset to first page when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Handle page change
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Handle items per page change
    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1); // Reset to first page
    };

    // Generate pagination items
    const generatePaginationItems = () => {
        const items = [];
        const maxVisiblePages = 5;
        
        // First page
        if (currentPage > 1) {
            items.push(
                <Pagination.First key="first" onClick={() => handlePageChange(1)} />
            );
            items.push(
                <Pagination.Prev key="prev" onClick={() => handlePageChange(currentPage - 1)} />
            );
        }
        
        // Calculate visible page range
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        
        // Adjust start page if we're near the end
        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }
        
        // Add ellipsis at the beginning if needed
        if (startPage > 1) {
            items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" />);
            }
        }
        
        // Add visible page numbers
        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }
        
        // Add ellipsis at the end if needed
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }
        
        // Next and Last
        if (currentPage < totalPages) {
            items.push(
                <Pagination.Next key="next" onClick={() => handlePageChange(currentPage + 1)} />
            );
            items.push(
                <Pagination.Last key="last" onClick={() => handlePageChange(totalPages)} />
            );
        }
        
        return items;
    };

    // Updated handle file selection with cropper
// ===== 4. HANDLE FILE SELECT FUNCTION =====
// Ganti fungsi handleFileSelect yang sudah ada dengan ini:

// ===== 5. HANDLE FILE SELECT DENGAN DEBUG =====
// Pastikan fungsi handleFileSelect Anda seperti ini:

const handleFileSelect = (e, fileType) => {
  const selectedFile = e.target.files[0];
  console.log(`=== FILE SELECT FOR ${fileType} ===`);
  
  if (selectedFile) {
    console.log('Selected file:', {
      name: selectedFile.name,
      size: selectedFile.size,
      type: selectedFile.type
    });

    // Validate file type
    if (!selectedFile.type.startsWith('image/')) {
      setError('Harap pilih file gambar yang valid');
      e.target.value = ''; // Clear input
      return;
    }

    // Validate file size (max 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('Ukuran file harus kurang dari 10MB');
      e.target.value = ''; // Clear input
      return;
    }

    // Clean up previous object URLs
    const currentState = cropperStates[fileType];
    if (currentState.originalImageUrl) {
      URL.revokeObjectURL(currentState.originalImageUrl);
      console.log('Revoked old original URL');
    }
    if (currentState.imagePreview) {
      URL.revokeObjectURL(currentState.imagePreview);
      console.log('Revoked old preview URL');
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    console.log('Created object URL:', objectUrl);
    
    setCropperStates(prev => ({
      ...prev,
      [fileType]: {
        selectedFile: null, // Clear previous cropped file
        imagePreview: null, // Clear previous preview
        showCropper: true,
        originalImageUrl: objectUrl
      }
    }));
    
    console.log(`Updated cropper state for ${fileType}`);
  }
};


    // Handle crop complete
  // ===== 3. HANDLE CROP COMPLETE FUNCTION =====
// Ganti fungsi handleCropComplete yang sudah ada dengan ini:

// ===== 2. HANDLE CROP COMPLETE DENGAN DEBUG =====
// Ganti fungsi handleCropComplete yang sudah ada dengan ini:

// ===== 5. HANDLE CROP COMPLETE UNTUK REACT-IMAGE-CROP =====
// Ganti fungsi handleCropComplete dengan ini:

const handleCropComplete = async (fileType, completedCrop, croppedBlob) => {
  try {
    console.log(`=== HANDLING CROP COMPLETE FOR ${fileType} ===`);
    console.log('Completed crop:', completedCrop);
    console.log('Cropped blob size:', croppedBlob.size);

    // Convert blob to file
    const timestamp = Date.now();
    const fileName = `${fileType}_cropped_${timestamp}.jpg`;
    const croppedFile = new File([croppedBlob], fileName, {
      type: 'image/jpeg',
      lastModified: timestamp,
    });

    // Create preview URL
    const previewUrl = URL.createObjectURL(croppedBlob);
    console.log('Preview URL created:', previewUrl);
    
    // Clean up old URLs
    const currentState = cropperStates[fileType];
    if (currentState.originalImageUrl) {
      URL.revokeObjectURL(currentState.originalImageUrl);
    }
    if (currentState.imagePreview) {
      URL.revokeObjectURL(currentState.imagePreview);
    }
    
    // Update state
    setCropperStates(prev => ({
      ...prev,
      [fileType]: {
        ...prev[fileType],
        selectedFile: croppedFile,
        imagePreview: previewUrl,
        showCropper: false
      }
    }));

    // Update backward compatibility states
    if (fileType === 'foto_profil') {
      setFotoProfil(croppedFile);
      setFotoProfilPreview(previewUrl);
    } else if (fileType === 'foto_ktp') {
      setFileKtp(croppedFile);
      setFileKtpPreview(previewUrl);
    } else if (fileType === 'foto_skck') {
      setFileskck(croppedFile);
      setFileskckPreview(previewUrl);
    }

    console.log(`Successfully handled crop for ${fileType}`);
    setSuccessMessage(`✅ ${fileType} berhasil di-crop!`);

  } catch (error) {
    console.error('Error in handleCropComplete:', error);
    setError('Gagal memproses hasil crop: ' + error.message);
  }
};

    // Clear image function
   // ===== 5. CLEAR IMAGE FUNCTION =====
// Ganti fungsi clearImage yang sudah ada dengan ini:

// ===== 6. CLEAR IMAGE DENGAN DEBUG =====
// Pastikan fungsi clearImage Anda seperti ini:

const clearImage = (fileType) => {
  console.log(`=== CLEARING IMAGE FOR ${fileType} ===`);
  
  // Clean up object URLs
  const currentState = cropperStates[fileType];
  if (currentState.originalImageUrl) {
    URL.revokeObjectURL(currentState.originalImageUrl);
    console.log('Revoked original URL');
  }
  if (currentState.imagePreview) {
    URL.revokeObjectURL(currentState.imagePreview);
    console.log('Revoked preview URL');
  }

  setCropperStates(prev => ({
    ...prev,
    [fileType]: {
      selectedFile: null,
      imagePreview: null,
      showCropper: false,
      originalImageUrl: null
    }
  }));

  // Clear file states (for backward compatibility)
  if (fileType === 'foto_profil') {
    setFotoProfil(null);
    setFotoProfilPreview(null);
  } else if (fileType === 'foto_ktp') {
    setFileKtp(null);
    setFileKtpPreview(null);
  } else if (fileType === 'foto_skck') {
    setFileskck(null);
    setFileskckPreview(null);
  }

  console.log(`Successfully cleared image for ${fileType}`);
};

    // Get crop configurations
    const getCropConfig = (fileType) => {
        const configs = {
            foto_profil: {
                aspectRatio: 1,
                cropShape: "round",
                title: "Crop Foto Profil"
            },
            foto_ktp: {
                aspectRatio: 16/9,
                cropShape: "rect", 
                title: "Crop Foto KTP"
            },
            foto_skck: {
                aspectRatio: 3/4,
                cropShape: "rect",
                title: "Crop Foto SKCK"
            }
        };
        return configs[fileType] || { aspectRatio: 1, cropShape: "rect", title: "Crop Image" };
    };

  // ===== 6. RENDER FILE INPUT WITH CROPPER FUNCTION =====
// Ganti fungsi renderFileInputWithCropper yang sudah ada dengan ini:

// ===== 4. RENDER FILE INPUT DENGAN DEBUG UI =====
// Ganti fungsi renderFileInputWithCropper yang sudah ada dengan ini:
// ===== 6. RENDER FILE INPUT UNTUK REACT-IMAGE-CROP =====
// Ganti fungsi renderFileInputWithCropper dengan ini:

const renderFileInputWithCropper = (fileType, label, required = false) => {
  const cropConfig = getCropConfig(fileType);
  const currentState = cropperStates[fileType];
  
  // Get current image URL for edit mode
  let currentImageUrl = null;
  if (fileType === 'foto_profil') currentImageUrl = currentMitra.foto_profil;
  else if (fileType === 'foto_ktp') currentImageUrl = currentMitra.url_foto_ktp;
  else if (fileType === 'foto_skck') currentImageUrl = currentMitra.url_foto_skck;

  // Priority: cropped image preview > current saved image
  const displayPreview = currentState.imagePreview || currentImageUrl;
  const hasNewCrop = !!currentState.imagePreview;

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label className="fw-bold d-flex align-items-center gap-2">
          {label}
          {hasNewCrop && (
            <span className="badge bg-success">
              <Icon icon="mdi:check" className="me-1" />
              Cropped
            </span>
          )}
        </Form.Label>
        
        <div className="d-flex align-items-center gap-2 mb-2">
          <Form.Control 
            type="file" 
            onChange={(e) => handleFileSelect(e, fileType)}
            required={required && !displayPreview}
            accept="image/*"
            className="flex-grow-1"
          />
          {displayPreview && (
            <Button 
              variant="outline-danger" 
              size="sm"
              onClick={() => clearImage(fileType)}
              title="Hapus Gambar"
            >
              <Icon icon="mdi:close" />
            </Button>
          )}
        </div>
        
        {displayPreview && (
          <div className="mt-3">
            <div className="row">
              <div className="col-auto">
                <div className="position-relative d-inline-block">
                  <img 
                    src={displayPreview} 
                    alt={`${label} Preview`} 
                    className={`img-thumbnail ${hasNewCrop ? 'border-success border-3' : 'border-secondary'}`}
                    style={{
                      width: '150px',
                      height: '150px',
                      objectFit: 'cover',
                      borderRadius: cropConfig.cropShape === 'round' ? '50%' : '8px'
                    }} 
                    onLoad={() => console.log(`Preview loaded for ${fileType}`)}
                    onError={(e) => {
                      console.error(`Preview failed for ${fileType}`);
                      e.target.src = "https://via.placeholder.com/150x150?text=Error";
                    }}
                  />
                  
                  {hasNewCrop && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-success">
                      <Icon icon="mdi:check" />
                    </span>
                  )}
                </div>
              </div>
              
              <div className="col">
                <div className="d-flex flex-column gap-2">
                  {currentState.originalImageUrl && (
                    <Button 
                      variant={hasNewCrop ? "outline-primary" : "primary"}
                      size="sm"
                      onClick={() => {
                        console.log(`Opening cropper for ${fileType}`);
                        setCropperStates(prev => ({
                          ...prev,
                          [fileType]: {
                            ...prev[fileType],
                            showCropper: true
                          }
                        }));
                      }}
                    >
                      <Icon icon="mdi:crop" className="me-1" />
                      {hasNewCrop ? 'Crop Ulang' : 'Crop Gambar'}
                    </Button>
                  )}
                  
                  {hasNewCrop && (
                    <div className="alert alert-success py-2 mb-0">
                      <small>
                        <Icon icon="mdi:check-circle" className="me-1" />
                        Gambar siap untuk diupload!
                      </small>
                    </div>
                  )}
                  
                  {!hasNewCrop && currentImageUrl && (
                    <div className="alert alert-warning py-2 mb-0">
                      <small>
                        <Icon icon="mdi:information" className="me-1" />
                        Menggunakan gambar tersimpan
                      </small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Form.Group>

      <ImageCropper
        show={currentState.showCropper}
        onHide={() => {
          console.log(`Closing cropper for ${fileType}`);
          setCropperStates(prev => ({
            ...prev,
            [fileType]: {
              ...prev[fileType],
              showCropper: false
            }
          }));
        }}
        imageUrl={currentState.originalImageUrl}
        onCropComplete={(completedCrop, croppedBlob) => {
          console.log(`Crop completed for ${fileType}`);
          handleCropComplete(fileType, completedCrop, croppedBlob);
        }}
        title={cropConfig.title}
        aspectRatio={cropConfig.aspectRatio}
        cropShape={cropConfig.cropShape}
      />
    </>
  );
};

    // Upload file to server
    const uploadFile = async (file) => {
        if (!file) return null;
        
        const formData = new FormData();
        formData.append("photo", file);
        
        try {
            console.log(`Uploading file...`);
            const response = await axios.post(UPLOAD_URL, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            console.log(`Upload result:`, response.data);
            
            // Check different possible response formats
            const fileUrl = response.data.fileUrl || response.data.url || response.data.photo_url;
            
            if (!fileUrl) {
                console.error('No file URL in response:', response.data);
                throw new Error('Server did not return a file URL');
            }
            
            return fileUrl;
        } catch (err) {
            console.error(`Error uploading file:`, err.response || err);
            throw new Error(`Failed to upload file: ${err.response?.data?.message || err.message}`);
        }
    };

    // Fetch mitras
    const fetchMitras = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/mitra`);
            setMitras(response.data.data);
            setFilteredMitras(response.data.data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching mitras:', err);
            setError('Gagal memuat daftar mitra');
            setLoading(false);
        }
    };

    // Search mitras
    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);
        
        if (!value.trim()) {
            setFilteredMitras(mitras);
            return;
        }
        
        const filtered = mitras.filter(
            mitra => 
                mitra.nama_lengkap.toLowerCase().includes(value) || 
                mitra.nomor_wa.toLowerCase().includes(value) ||
                mitra.alamat.toLowerCase().includes(value) ||
                (mitra.asal_pb && mitra.asal_pb.toLowerCase().includes(value)) ||
                (mitra.reg_via && mitra.reg_via.toLowerCase().includes(value)) ||
                mitra.id.toString().includes(value)
        );
        
        setFilteredMitras(filtered);
    };

    // Reset search
    const resetSearch = () => {
        setSearchTerm('');
        setFilteredMitras(mitras);
        setCurrentPage(1);
    };

    // Initial fetch
    useEffect(() => {
        fetchMitras();
    }, []);

    // Updated reset form function
    const resetForm = () => {
        setCurrentMitra({
            nama_lengkap: '',
            jenis_kelamin: '',
            nomor_wa: '',
            alamat: '',
            url_foto_skck: '',
            url_foto_ktp: '',
            foto_profil: '',
            password: '',
            radius: 5000,
            status: 'active',
            asal_pb: '',
            preferences: {
                gender: ['Pria', 'Wanita']
            }
        });
        
        // Reset file states
        setFileskck(null);
        setFileKtp(null);
        setFotoProfil(null);
        setFileskckPreview(null);
        setFileKtpPreview(null);
        setFotoProfilPreview(null);
        
        // Reset cropper states
        setCropperStates({
            foto_profil: {
                selectedFile: null,
                imagePreview: null,
                showCropper: false,
                originalImageUrl: null
            },
            foto_ktp: {
                selectedFile: null,
                imagePreview: null,
                showCropper: false,
                originalImageUrl: null
            },
            foto_skck: {
                selectedFile: null,
                imagePreview: null,
                showCropper: false,
                originalImageUrl: null
            }
        });
        
        setError(null);
        setSuccessMessage(null);
    };

  const handleAddMitra = async (e) => {
    e.preventDefault();
    setError(null);
    setIsAddingMitra(true); // Set loading state
    
    try {
        // Validate required files from cropper states
        const fotoProfilFile = cropperStates.foto_profil.selectedFile;
        const fotoKtpFile = cropperStates.foto_ktp.selectedFile;
        const fotoskckFile = cropperStates.foto_skck.selectedFile;
        
        if (!fotoProfilFile || !fotoKtpFile || !fotoskckFile) {
            setError('Semua file (Profil, KTP, dan SKCK) wajib diunggah dan di-crop');
            return;
        }
        
        // Upload files dengan files yang sudah di-crop
        let skckUrl, ktpUrl, profilUrl;
        
        try {
            // Update progress message
            setSuccessMessage('Mengupload foto profil...');
            profilUrl = await uploadFile(fotoProfilFile);
            console.log('Profil URL:', profilUrl);
            
            setSuccessMessage('Mengupload foto KTP...');
            ktpUrl = await uploadFile(fotoKtpFile);
            console.log('KTP URL:', ktpUrl);
            
            setSuccessMessage('Mengupload foto SKCK...');
            skckUrl = await uploadFile(fotoskckFile);
            console.log('SKCK URL:', skckUrl);
            
            setSuccessMessage('Menyimpan data mitra...');
        } catch (uploadErr) {
            setError(uploadErr.message);
            return;
        }

        // Prepare mitra data
        const mitraData = {
            ...currentMitra,
            url_foto_skck: skckUrl,
            url_foto_ktp: ktpUrl,
            foto_profil: profilUrl
        };

        console.log('Sending mitra data:', mitraData);

        // Send registration request
        const response = await axios.post(`${API_BASE_URL}/mitra/register`, mitraData);
        
        if (response.data.success) {
            fetchMitras();
            setShowAddModal(false);
            setSuccessMessage('Mitra berhasil ditambahkan');
            resetForm();
        } else {
            setError(response.data.message || 'Gagal menambahkan mitra');
        }
    } catch (err) {
        console.error('Error adding mitra:', err);
        setError(err.response?.data?.message || 'Gagal menambahkan mitra');
    } finally {
        setIsAddingMitra(false); // Reset loading state
    }
};

// Updated Edit Mitra function dengan loading state
const handleEditMitra = async (e) => {
    e.preventDefault();
    setError(null);
    setIsEditingMitra(true); // Set loading state
    
    try {
        let skckUrl = currentMitra.url_foto_skck;
        let ktpUrl = currentMitra.url_foto_ktp;
        let profilUrl = currentMitra.foto_profil;
        
        // Upload new files if selected from cropper
        try {
            if (cropperStates.foto_skck.selectedFile) {
                setSuccessMessage('Mengupload foto SKCK baru...');
                skckUrl = await uploadFile(cropperStates.foto_skck.selectedFile);
                console.log('New SKCK URL:', skckUrl);
            }
            
            if (cropperStates.foto_ktp.selectedFile) {
                setSuccessMessage('Mengupload foto KTP baru...');
                ktpUrl = await uploadFile(cropperStates.foto_ktp.selectedFile);
                console.log('New KTP URL:', ktpUrl);
            }
            
            if (cropperStates.foto_profil.selectedFile) {
                setSuccessMessage('Mengupload foto profil baru...');
                profilUrl = await uploadFile(cropperStates.foto_profil.selectedFile);
                console.log('New Profil URL:', profilUrl);
            }
            
            setSuccessMessage('Memperbarui data mitra...');
        } catch (uploadErr) {
            setError(uploadErr.message);
            return;
        }

        // Prepare mitra data
        const mitraData = {
            ...currentMitra,
            url_foto_skck: skckUrl,
            url_foto_ktp: ktpUrl,
            foto_profil: profilUrl
        };

        // Remove password if not changed
        if (!mitraData.password) {
            delete mitraData.password;
        }

        console.log('Sending updated mitra data:', mitraData);

        // Send update request
        const response = await axios.put(`${API_BASE_URL}/mitra/${currentMitra.id}`, mitraData);
        
        if (response.data.success) {
            fetchMitras();
            setShowEditModal(false);
            setSuccessMessage('Mitra berhasil diperbarui');
            resetForm();
        } else {
            setError(response.data.message || 'Gagal memperbarui mitra');
        }
    } catch (err) {
        console.error('Error editing mitra:', err);
        setError(err.response?.data?.message || 'Gagal memperbarui mitra');
    } finally {
        setIsEditingMitra(false); // Reset loading state
    }
};

// Updated Delete Mitra function dengan loading state
const handleDeleteMitra = async () => {
    setIsDeletingMitra(true); // Set loading state
    
    try {
        const response = await axios.delete(`${API_BASE_URL}/mitra/${currentMitra.id}`);
        
        if (response.data.success) {
            fetchMitras();
            setShowDeleteModal(false);
            setSuccessMessage('Mitra berhasil dihapus');
            resetForm();
        } else {
            setError(response.data.message || 'Gagal menghapus mitra');
        }
    } catch (err) {
        console.error('Error deleting mitra:', err);
        setError(err.response?.data?.message || 'Gagal menghapus mitra');
    } finally {
        setIsDeletingMitra(false); // Reset loading state
    }
};

// Loading Overlay Component
const LoadingOverlay = ({ show, message = "Memproses..." }) => {
    if (!show) return null;
    
    return (
        <div 
            className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
            style={{
                top: 0,
                left: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                zIndex: 1050,
                borderRadius: '0.375rem'
            }}
        >
            <div className="text-center">
                <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div className="fw-medium text-primary">{message}</div>
            </div>
        </div>
    );
};
    
    // Activate Mitra Status
    const handleActivateMitra = async (mitraId) => {
        try {
            const response = await axios.patch(`${API_BASE_URL}/mitra/${mitraId}/status`, {
                status: 'active'
            });
            
            if (response.data.success) {
                fetchMitras();
                setSuccessMessage('Status mitra berhasil diaktifkan');
            } else {
                setError(response.data.message || 'Gagal mengaktifkan status mitra');
            }
        } catch (err) {
            console.error('Error activating mitra:', err);
            setError(err.response?.data?.message || 'Gagal mengaktifkan status mitra');
        }
    };

    // Deactivate Mitra Status
    const handleDeactivateMitra = async (mitraId) => {
        try {
            const response = await axios.patch(`${API_BASE_URL}/mitra/${mitraId}/status`, {
                status: 'nonactive'
            });
            
            if (response.data.success) {
                fetchMitras();
                setSuccessMessage('Status mitra berhasil dinonaktifkan');
            } else {
                setError(response.data.message || 'Gagal menonaktifkan status mitra');
            }
        } catch (err) {
            console.error('Error deactivating mitra:', err);
            setError(err.response?.data?.message || 'Gagal menonaktifkan status mitra');
        }
    };

    // Handle Excel Import
    const handleExcelSelect = (e) => {
        const file = e.target.files[0];
        setExcelFile(file);
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = e.target.result;
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                    
                    if (json.length < 2) {
                        setError('File Excel tidak valid atau kosong');
                        return;
                    }
                    
                    // Get headers from the first row
                    const headers = json[0];
                    
                    // Expected headers
                    const expectedHeaders = ['nama_lengkap', 'jenis_kelamin', 'nomor_wa', 'alamat', 'asal_pb', 'radius', 'password'];
                    
                    // Validate headers
                    const missingHeaders = expectedHeaders.filter(header => !headers.includes(header));
                    if (missingHeaders.length > 0) {
                        setError(`File Excel tidak valid. Kolom berikut tidak ditemukan: ${missingHeaders.join(', ')}`);
                        return;
                    }
                    
                    // Process data rows
                    const dataRows = json.slice(1).map(row => {
                        const dataObj = {};
                        headers.forEach((header, index) => {
                            dataObj[header] = row[index] || '';
                        });
                        return dataObj;
                    });
                    
                    // Validate data
                    const errors = [];
                    dataRows.forEach((row, index) => {
                        if (!row.nama_lengkap) {
                            errors.push(`Baris ${index + 2}: Nama lengkap tidak boleh kosong`);
                        }
                        if (!row.jenis_kelamin || !['Laki-laki', 'Perempuan'].includes(row.jenis_kelamin)) {
                            errors.push(`Baris ${index + 2}: Jenis kelamin harus 'Laki-laki' atau 'Perempuan'`);
                        }
                        if (!row.nomor_wa) {
                            errors.push(`Baris ${index + 2}: Nomor WA tidak boleh kosong`);
                        }
                        if (!row.alamat) {
                            errors.push(`Baris ${index + 2}: Alamat tidak boleh kosong`);
                        }
                        if (!row.password) {
                            errors.push(`Baris ${index + 2}: Password tidak boleh kosong`);
                        }
                    });
                    
                    setImportValidationErrors(errors);
                    setImportData(dataRows);
                    
                } catch (error) {
                    console.error('Error parsing Excel:', error);
                    setError('Error memproses file Excel');
                }
            };
            reader.readAsArrayBuffer(file);
        }
    };

    // Handle Import Submit
    const handleImportSubmit = async () => {
        if (importValidationErrors.length > 0) {
            setError('Harap perbaiki error validasi sebelum melanjutkan import');
            return;
        }
        
        setIsImporting(true);
        setError(null);
        
        try {
            let successCount = 0;
            let errorCount = 0;
            const importErrors = [];
            
            // Process each row
            for (const [index, row] of importData.entries()) {
                try {
                    // Set status and preferences
                    const mitraData = {
                        ...row,
                        status: 'active',
                        preferences: {
                            gender: ['Pria', 'Wanita']
                        },
                        // Set empty placeholder for required images
                        url_foto_skck: 'https://via.placeholder.com/150?text=skck',
                        url_foto_ktp: 'https://via.placeholder.com/150?text=KTP',
                        foto_profil: 'https://via.placeholder.com/150?text=Profil'
                    };
                    
                    // Convert radius to number
                    if (row.radius) {
                        mitraData.radius = parseInt(row.radius);
                    } else {
                        mitraData.radius = 5000; // Default value
                    }
                    
                    // Send request
                    const response = await axios.post(`${API_BASE_URL}/mitra/register`, mitraData);
                    
                    if (response.data.success) {
                        successCount++;
                    } else {
                        errorCount++;
                        importErrors.push(`Baris ${index + 2}: ${response.data.message || 'Gagal menambahkan'}`);
                    }
                } catch (err) {
                    errorCount++;
                    importErrors.push(`Baris ${index + 2}: ${err.response?.data?.message || err.message}`);
                }
            }
            
            // Update UI
            if (errorCount > 0) {
                setError(`Import selesai dengan ${errorCount} error. ${importErrors.slice(0, 5).join('. ')}${importErrors.length > 5 ? ' dan lainnya...' : ''}`);
            }
            
            if (successCount > 0) {
                setSuccessMessage(`Berhasil mengimport ${successCount} mitra`);
                fetchMitras();
                setShowImportModal(false);
                
                // Reset import form
                setExcelFile(null);
                setImportData([]);
                setImportValidationErrors([]);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        } catch (err) {
            console.error('Error during import:', err);
            setError('Terjadi kesalahan saat import');
        } finally {
            setIsImporting(false);
        }
    };

    // Create example Excel template for download
    const generateExcelTemplate = () => {
        // Define columns
        const headers = ['nama_lengkap', 'jenis_kelamin', 'nomor_wa', 'alamat', 'asal_pb', 'radius', 'password'];
        
        // Sample data
        const sampleData = [
            {
                nama_lengkap: 'John Doe',
                jenis_kelamin: 'Laki-laki',
                nomor_wa: '6281234567890',
                alamat: 'Jl. Contoh No. 123, Jakarta',
                asal_pb: 'Jakarta Selatan',
                radius: 5000,
                password: 'password123'
            },
            {
                nama_lengkap: 'Jane Smith',
                jenis_kelamin: 'Perempuan',
                nomor_wa: '6289876543210',
                alamat: 'Jl. Sample No. 456, Bandung',
                asal_pb: 'Bandung',
                radius: 3000,
                password: 'securepass456'
            }
        ];
        
        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet(sampleData, { header: headers });
        
        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Mitra Template');
        
        // Generate XLSX file
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        
        // Convert to blob and download
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'mitra_import_template.xlsx';
        link.click();
        
        // Clean up
        URL.revokeObjectURL(url);
    };

    // Render loading
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid p-4">
            {/* Success Message */}
            {successMessage && (
                <Alert 
                    variant="success" 
                    onClose={() => setSuccessMessage(null)} 
                    dismissible
                >
                    {successMessage}
                </Alert>
            )}

            {/* Error Message */}
            {error && (
                <Alert 
                    variant="danger" 
                    onClose={() => setError(null)} 
                    dismissible
                >
                    {error}
                </Alert>
            )}

            <div className="card shadow-sm">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">
                        <Icon icon="mdi:account-group" className="me-2" />
                        Daftar Mitra
                    </h4>
                    <div>
                        <Button 
                            variant="outline-light" 
                            className="me-2"
                            onClick={() => setShowImportModal(true)}
                        >
                            <Icon icon="mdi:file-import" className="me-1" />
                            Import Excel
                        </Button>
                        <Button 
                            variant="light" 
                            onClick={() => {
                                resetForm();
                                setShowAddModal(true);
                            }}
                        >
                            <Icon icon="mdi:plus" className="me-1" />
                            Tambah Mitra
                        </Button>
                    </div>
                </div>
                
                <div className="card-body">
                    {/* Search Box and Pagination Controls */}
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <InputGroup>
                                <Form.Control
                                    placeholder="Cari mitra berdasarkan nama, nomor WA, alamat, asal PB, atau ID..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                                {searchTerm && (
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={resetSearch}
                                    >
                                        <Icon icon="mdi:close" />
                                    </Button>
                                )}
                                <Button variant="outline-primary">
                                    <Icon icon="mdi:magnify" />
                                </Button>
                            </InputGroup>
                        </div>
                        <div className="col-md-6 d-flex justify-content-end align-items-center">
                            <div className="me-3">
                                <Form.Label className="me-2 mb-0">Tampilkan:</Form.Label>
                                <Form.Select 
                                    size="sm" 
                                    style={{width: 'auto', display: 'inline-block'}}
                                    value={itemsPerPage}
                                    onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </Form.Select>
                                <span className="ms-2">per halaman</span>
                            </div>
                            {filteredMitras.length !== mitras.length && (
                                <span className="text-muted">
                                    Menampilkan {filteredMitras.length} dari {mitras.length} mitra
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Pagination Info */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <small className="text-muted">
                                Menampilkan {paginatedMitras.length > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} - {Math.min(currentPage * itemsPerPage, filteredMitras.length)} dari {filteredMitras.length} data
                            </small>
                        </div>
                    </div>
                    
                    <div className="table-responsive">
                        <table className="table table-striped table-hover mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>ID</th>
                                    <th>Nama Lengkap</th>
                                    <th>Asal PB</th>
                                    <th>Jenis Kelamin</th>
                                    <th>Nomor WA</th>
                                    <th>Status</th>
                                    <th>Registrasi Via</th>
                                    <th>Tanggal Dibuat</th>
                                    <th className="text-center">Aksi</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedMitras.length > 0 ? (
                                    paginatedMitras.map((mitra) => (
                                        <tr key={mitra.id}>
                                            <td>{mitra.id}</td>
                                            <td>{mitra.nama_lengkap}</td>
                                            <td>{mitra.asal_pb}</td>
                                            <td>{mitra.jenis_kelamin}</td>
                                            <td>{mitra.nomor_wa}</td>
                                            <td>
                                                <span 
                                                    className={`badge ${mitra.status === 'active' ? 'bg-success' : 'bg-warning'}`}
                                                >
                                                    {mitra.status}
                                                </span>
                                            </td>
                                            <td>
                                                {/* Display registration source with badge */}
                                                {mitra.reg_via ? (
                                                    <span className={`badge ${
                                                        mitra.reg_via === 'adminlink' ? 'bg-info' : 
                                                        mitra.reg_via === 'app' ? 'bg-primary' : 'bg-secondary'
                                                    }`}>
                                                        {mitra.reg_via === 'adminlink' ? 'Admin Link' : 
                                                         mitra.reg_via === 'app' ? 'Aplikasi' : mitra.reg_via}
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-secondary">Unknown</span>
                                                )}
                                            </td>
                                            <td>
                                                {new Date(mitra.created_at).toLocaleDateString('id-ID', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </td>
                                            <td className="text-center">
                                                <div className="btn-group" role="group">
                                                    {/* Activate button - only shown for nonactive mitras */}
                                                    {mitra.status === 'nonactive' && (
                                                        <Button 
                                                            variant="success" 
                                                            size="sm"
                                                            className="me-1"
                                                            onClick={() => handleActivateMitra(mitra.id)}
                                                            title="Aktifkan Mitra"
                                                        >
                                                            <Icon icon="mdi:check-circle" />
                                                        </Button>
                                                    )}
                                                    
                                                    {/* Deactivate button - only shown for active mitras */}
                                                    {mitra.status === 'active' && (
                                                        <Button 
                                                            variant="warning" 
                                                            size="sm"
                                                            className="me-1"
                                                            onClick={() => handleDeactivateMitra(mitra.id)}
                                                            title="Nonaktifkan Mitra"
                                                        >
                                                            <Icon icon="mdi:block-helper" />
                                                        </Button>
                                                    )}
                                                    
                                                    <Button 
                                                        variant="info" 
                                                        size="sm" 
                                                        className="me-1"
                                                        onClick={() => {
                                                            setCurrentMitra(mitra);
                                                            setShowDetailModal(true);
                                                        }}
                                                    >
                                                        <Icon icon="mdi:eye" />
                                                    </Button>
                                                    <Button 
                                                        variant="warning" 
                                                        size="sm" 
                                                        className="me-1"
                                                        onClick={() => {
                                                            setCurrentMitra({...mitra, password: ''});
                                                            setShowEditModal(true);
                                                        }}
                                                    >
                                                        <Icon icon="mdi:pencil" />
                                                    </Button>
                                                    <Button 
                                                        variant="danger" 
                                                        size="sm"
                                                        onClick={() => {
                                                            setCurrentMitra(mitra);
                                                            setShowDeleteModal(true);
                                                        }}
                                                    >
                                                        <Icon icon="mdi:delete" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="9" className="text-center">
                                            {searchTerm ? (
                                                <>
                                                    <Icon icon="mdi:file-search-outline" className="me-2" size="24" />
                                                    Tidak ada mitra yang cocok dengan pencarian
                                                </>
                                            ) : (
                                                'Tidak ada data mitra'
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Component */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                            <Pagination>
                                {generatePaginationItems()}
                            </Pagination>
                        </div>
                    )}
                </div>
            </div>

            {/* Add Mitra Modal with Cropper */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <Icon icon="mdi:account-plus" className="me-2" />
                        Tambah Mitra Baru
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleAddMitra}>
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Nama Lengkap</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        value={currentMitra.nama_lengkap}
                                        onChange={(e) => setCurrentMitra({...currentMitra, nama_lengkap: e.target.value})}
                                        required 
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Asal PB</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        value={currentMitra.asal_pb}
                                        onChange={(e) => setCurrentMitra({...currentMitra, asal_pb: e.target.value})}
                                        required 
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Jenis Kelamin</Form.Label>
                                    <Form.Select 
                                        value={currentMitra.jenis_kelamin}
                                        onChange={(e) => setCurrentMitra({...currentMitra, jenis_kelamin: e.target.value})}
                                        required
                                    >
                                        <option value="">Pilih Jenis Kelamin</option>
                                        <option value="Laki-laki">Laki-laki</option>
                                        <option value="Perempuan">Perempuan</option>
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nomor WA</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        value={currentMitra.nomor_wa}
                                        onChange={(e) => setCurrentMitra({...currentMitra, nomor_wa: e.target.value})}
                                        required 
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Alamat</Form.Label>
                                    <Form.Control 
                                        as="textarea"
                                        rows={3}
                                        value={currentMitra.alamat}
                                        onChange={(e) => setCurrentMitra({...currentMitra, alamat: e.target.value})}
                                        required 
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Password</Form.Label>
                                    <Form.Control 
                                        type="password" 
                                        value={currentMitra.password}
                                        onChange={(e) => setCurrentMitra({...currentMitra, password: e.target.value})}
                                        required 
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Radius Layanan (meter)</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        value={currentMitra.radius}
                                        onChange={(e) => setCurrentMitra({...currentMitra, radius: parseInt(e.target.value)})}
                                        required 
                                        min="500"
                                        max="50000"
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        value={currentMitra.status}
                                        onChange={(e) => setCurrentMitra({...currentMitra, status: e.target.value})}
                                        required
                                    >
                                        <option value="active">Active</option>
                                        <option value="nonactive">Nonactive</option>
                                    </Form.Select>
                                </Form.Group>
                            </div>
                            
                            {/* File Inputs with Cropper */}
                            <div className="col-md-6">
                                {renderFileInputWithCropper('foto_profil', 'Foto Profil', true)}
                                {renderFileInputWithCropper('foto_ktp', 'Foto KTP', true)}
                                {renderFileInputWithCropper('foto_skck', 'Foto skck', true)}
                            </div>
                        </div>
                        <div className="d-flex justify-content-between mt-3">
                            <Button variant="primary" type="submit">
                                <Icon icon="mdi:plus" className="me-1" />
                                Tambah Mitra
                            </Button>
                            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                                Batal
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Edit Mitra Modal with Cropper */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <Icon icon="mdi:account-edit" className="me-2" />
                        Edit Mitra
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleEditMitra}>
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Nama Lengkap</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        value={currentMitra.nama_lengkap}
                                        onChange={(e) => setCurrentMitra({...currentMitra, nama_lengkap: e.target.value})}
                                        required 
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Asal PB</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        value={currentMitra.asal_pb}
                                        onChange={(e) => setCurrentMitra({...currentMitra, asal_pb: e.target.value})}
                                        required 
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Jenis Kelamin</Form.Label>
                                    <Form.Select 
                                        value={currentMitra.jenis_kelamin}
                                        onChange={(e) => setCurrentMitra({...currentMitra, jenis_kelamin: e.target.value})}
                                        required
                                    >
                                        <option value="">Pilih Jenis Kelamin</option>
                                        <option value="Laki-laki">Laki-laki</option>
                                        <option value="Perempuan">Perempuan</option>
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nomor WA</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        value={currentMitra.nomor_wa}
                                        onChange={(e) => setCurrentMitra({...currentMitra, nomor_wa: e.target.value})}
                                        required 
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Alamat</Form.Label>
                                    <Form.Control 
                                        as="textarea"
                                        rows={3}
                                        value={currentMitra.alamat}
                                        onChange={(e) => setCurrentMitra({...currentMitra, alamat: e.target.value})}
                                        required 
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Password (Kosongkan jika tidak diubah)</Form.Label>
                                    <Form.Control 
                                        type="password" 
                                        value={currentMitra.password || ''}
                                        onChange={(e) => setCurrentMitra({...currentMitra, password: e.target.value})}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Radius Layanan (meter)</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        value={currentMitra.radius}
                                        onChange={(e) => setCurrentMitra({...currentMitra, radius: parseInt(e.target.value)})}
                                        required 
                                        min="500"
                                        max="50000"
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        value={currentMitra.status}
                                        onChange={(e) => setCurrentMitra({...currentMitra, status: e.target.value})}
                                        required
                                    >
                                        <option value="active">Active</option>
                                        <option value="nonactive">Nonactive</option>
                                    </Form.Select>
                                </Form.Group>
                            </div>
                            
                            {/* File Inputs with Cropper */}
                            <div className="col-md-6">
                                {renderFileInputWithCropper('foto_profil', 'Foto Profil', false)}
                                {renderFileInputWithCropper('foto_ktp', 'Foto KTP', false)}
                                {renderFileInputWithCropper('foto_skck', 'Foto skck', false)}
                            </div>
                        </div>
                        <div className="d-flex justify-content-between mt-3">
                            <Button variant="primary" type="submit">
                                <Icon icon="mdi:content-save" className="me-1" />
                                Simpan Perubahan
                            </Button>
                            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                                Batal
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Detail Mitra Modal */}
            <Modal show={showDetailModal} onHide={() => {
                setShowDetailModal(false);
                setActiveDetailTab('info');
            }} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Detail Mitra - {currentMitra.nama_lengkap}</Modal.Title>
                </Modal.Header>
                
                <div className="px-3 pt-3 border-bottom">
                    <ul className="nav nav-tabs">
                        <li className="nav-item">
                            <button 
                                className={`nav-link ${activeDetailTab === 'info' ? 'active' : ''}`}
                                onClick={() => setActiveDetailTab('info')}
                            >
                                <Icon icon="mdi:account" className="me-1" />
                                Informasi Mitra
                            </button>
                        </li>
                        <li className="nav-item">
                            <button 
                                className={`nav-link ${activeDetailTab === 'services' ? 'active' : ''}`}
                                onClick={() => setActiveDetailTab('services')}
                            >
                                <Icon icon="mdi:spa" className="me-1" />
                                Layanan
                            </button>
                        </li>
                    </ul>
                </div>
                
                <Modal.Body className="p-0">
                    {activeDetailTab === 'info' ? (
                        <div className="p-3">
                            <div className="row">
                                <div className="col-md-4">
                                    <h5>Foto Profil</h5>
                                    {currentMitra.foto_profil ? (
                                        <img 
                                            src={currentMitra.foto_profil} 
                                            alt="Foto Profil" 
                                            className="img-fluid rounded mb-3" 
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = "https://via.placeholder.com/150?text=No+Image";
                                            }}
                                        />
                                    ) : (
                                        <p>Tidak ada foto profil</p>
                                    )}
                                    
                                    <h5>Foto KTP</h5>
                                    {currentMitra.url_foto_ktp ? (
                                        <img 
                                            src={currentMitra.url_foto_ktp} 
                                            alt="Foto KTP" 
                                            className="img-fluid rounded mb-3" 
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = "https://via.placeholder.com/150?text=No+Image";
                                            }}
                                        />
                                    ) : (
                                        <p>Tidak ada foto KTP</p>
                                    )}
                                    
                                    <h5>Foto skck</h5>
                                    {currentMitra.url_foto_skck ? (
                                        <img 
                                            src={currentMitra.url_foto_skck} 
                                            alt="Foto skck" 
                                            className="img-fluid rounded" 
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = "https://via.placeholder.com/150?text=No+Image";
                                            }}
                                        />
                                    ) : (
                                        <p>Tidak ada foto skck</p>
                                    )}
                                </div>
                                <div className="col-md-8">
                                    <table className="table">
                                        <tbody>
                                            <tr>
                                                <th>ID</th>
                                                <td>{currentMitra.id}</td>
                                            </tr>
                                            <tr>
                                                <th>Nama Lengkap</th>
                                                <td>{currentMitra.nama_lengkap}</td>
                                            </tr>
                                            <tr>
                                                <th>Asal PB</th>
                                                <td>{currentMitra.asal_pb}</td>
                                            </tr>
                                            <tr>
                                                <th>Jenis Kelamin</th>
                                                <td>{currentMitra.jenis_kelamin}</td>
                                            </tr>
                                            <tr>
                                                <th>Nomor WA</th>
                                                <td>{currentMitra.nomor_wa}</td>
                                            </tr>
                                            <tr>
                                                <th>Alamat</th>
                                                <td>{currentMitra.alamat}</td>
                                            </tr>
                                            <tr>
                                                <th>Status</th>
                                                <td>
                                                    <span 
                                                        className={`badge ${currentMitra.status === 'active' ? 'bg-success' : 'bg-warning'}`}
                                                    >
                                                        {currentMitra.status}
                                                    </span>
                                                    {currentMitra.status === 'nonactive' && (
                                                        <Button 
                                                            variant="success" 
                                                            size="sm"
                                                            className="ms-2"
                                                            onClick={() => {
                                                                handleActivateMitra(currentMitra.id);
                                                                setShowDetailModal(false);
                                                            }}
                                                        >
                                                            Aktifkan
                                                        </Button>
                                                    )}
                                                    {currentMitra.status === 'active' && (
                                                        <Button 
                                                            variant="warning" 
                                                            size="sm"
                                                            className="ms-2"
                                                            onClick={() => {
                                                                handleDeactivateMitra(currentMitra.id);
                                                                setShowDetailModal(false);
                                                            }}
                                                        >
                                                            Nonaktifkan
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>Radius Layanan</th>
                                                <td>{currentMitra.radius} meter</td>
                                            </tr>
                                            <tr>
                                                <th>Tanggal Bergabung</th>
                                                <td>
                                                    {currentMitra.created_at ? new Date(currentMitra.created_at).toLocaleString('id-ID', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    }) : 'N/A'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>Preferensi Gender</th>
                                                <td>{currentMitra.preferences?.gender?.join(', ') || 'Tidak diset'}</td>
                                            </tr>
                                            <tr>
                                                <th>Lokasi</th>
                                                <td>
                                                    {currentMitra.latitude && currentMitra.longitude ? 
                                                        `Latitude: ${currentMitra.latitude}, Longitude: ${currentMitra.longitude}` :
                                                        'Lokasi belum diset'
                                                    }
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="p-3">
                            <MitraServices mitraId={currentMitra.id} />
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {activeDetailTab === 'info' && (
                        <Button 
                            variant="warning" 
                            onClick={() => {
                                setCurrentMitra({...currentMitra, password: ''});
                                setShowDetailModal(false);
                                setShowEditModal(true);
                            }}
                        >
                            <Icon icon="mdi:pencil" className="me-1" />
                            Edit Mitra
                        </Button>
                    )}
                    <Button 
                        variant="secondary" 
                        onClick={() => {
                            setShowDetailModal(false);
                            setActiveDetailTab('info');
                        }}
                    >
                        Tutup
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Konfirmasi Hapus</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Apakah Anda yakin ingin menghapus mitra <strong>{currentMitra.nama_lengkap}</strong>?</p>
                    <p className="text-danger">
                        <Icon icon="mdi:alert" className="me-1" />
                        Tindakan ini tidak dapat dibatalkan!
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Batal
                    </Button>
                    <Button variant="danger" onClick={handleDeleteMitra}>
                        <Icon icon="mdi:delete" className="me-1" />
                        Hapus
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Import Excel Modal */}
            <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Import Mitra dari Excel</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="info">
                        <Icon icon="mdi:information" className="me-2" />
                        Upload file Excel dengan format yang sesuai. Download template untuk memudahkan.
                    </Alert>

                    <Button 
                        variant="outline-primary" 
                        className="mb-3"
                        onClick={generateExcelTemplate}
                    >
                        <Icon icon="mdi:file-download" className="me-1" />
                        Download Template Excel
                    </Button>

                    <Form.Group className="mb-3">
                        <Form.Label>Pilih File Excel</Form.Label>
                        <Form.Control 
                            type="file" 
                            ref={fileInputRef}
                            onChange={handleExcelSelect}
                            accept=".xlsx, .xls" 
                        />
                        <Form.Text className="text-muted">
                            Format yang didukung: .xlsx, .xls
                        </Form.Text>
                    </Form.Group>

                    {importValidationErrors.length > 0 && (
                        <Alert variant="danger">
                            <h6 className="alert-heading">Error Validasi:</h6>
                            <ul className="mb-0">
                                {importValidationErrors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </Alert>
                    )}

                    {importData.length > 0 && (
                        <div className="mt-3">
                            <h6>Preview Data ({importData.length} baris):</h6>
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Nama Lengkap</th>
                                            <th>Asal PB</th>
                                            <th>Jenis Kelamin</th>
                                            <th>Nomor WA</th>
                                            <th>Alamat</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {importData.slice(0, 3).map((row, index) => (
                                            <tr key={index}>
                                                <td>{row.nama_lengkap}</td>
                                                <td>{row.asal_pb}</td>
                                                <td>{row.jenis_kelamin}</td>
                                                <td>{row.nomor_wa}</td>
                                                <td>{row.alamat}</td>
                                            </tr>
                                        ))}
                                        {importData.length > 3 && (
                                            <tr>
                                                <td colSpan="5" className="text-center">
                                                    ...dan {importData.length - 3} baris lainnya
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowImportModal(false)}>
                        Batal
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleImportSubmit}
                        disabled={importData.length === 0 || isImporting}
                    >
                        {isImporting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Importing...
                            </>
                        ) : (
                            <>
                                <Icon icon="mdi:upload" className="me-1" />
                                Import {importData.length} Mitra
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default MitraList;